import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Star } from "lucide-react";
import api from "@/lib/api";
import { setSeo } from "@/lib/events";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AdSlot } from "@/components/AdSlot";
import { MobileBirthdayBar } from "@/components/MobileBirthdayBar";
import { ZODIAC, ZODIAC_GLYPH } from "@/components/HoroscopePanel";
import { BirthdayLink } from "@/components/BirthdayCaptureModal";

const DATES = {
  aries: "Mar 21 - Apr 19", taurus: "Apr 20 - May 20", gemini: "May 21 - Jun 20", cancer: "Jun 21 - Jul 22",
  leo: "Jul 23 - Aug 22", virgo: "Aug 23 - Sep 22", libra: "Sep 23 - Oct 22", scorpio: "Oct 23 - Nov 21",
  sagittarius: "Nov 22 - Dec 21", capricorn: "Dec 22 - Jan 19", aquarius: "Jan 20 - Feb 18", pisces: "Feb 19 - Mar 20",
};

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const prettyDate = (ddmm) => { const [d, m] = (ddmm || "").split("-").map(Number); return d && m ? `${MONTHS_SHORT[m - 1]} ${d}` : ddmm; };
const personName = (title) => (title || "").replace(/^(Birth|Death) of /i, "").replace(/ born$/i, "");

const TraitList = ({ items, kind }) => (
  <ul className={`${kind}-list`} data-testid={`${kind}-list`}>
    {(items || []).map((x) => {
      const [label, ...rest] = String(x).split(":");
      const text = rest.join(":").trim();
      return <li key={x}>{text ? <><strong>{label.trim()}:</strong> {text}</> : x}</li>;
    })}
  </ul>
);

const Bio = ({ sign, bio }) => {
  const [more, setMore] = useState(false);
  if (!bio) return null;
  return (
    <div className="mt-3 text-[13px]" data-testid={`bio-${sign}`}>
      {bio.intro && <p className="text-ink leading-relaxed">{bio.intro}</p>}
      <p className="mt-2 text-[12px] text-ink-muted">
        <span className="font-semibold text-ink">Dates:</span> {bio.dates} • <span className="font-semibold text-ink">Symbol:</span> {bio.symbol} • <span className="font-semibold text-ink">Element:</span> {bio.element} • <span className="font-semibold text-ink">Ruling Body:</span> {bio.ruling_body || bio.ruler}
      </p>
      <button type="button" data-testid={`bio-toggle-${sign}`} onClick={() => setMore(!more)} aria-expanded={more} className="mt-2 text-[12px] font-semibold text-gold hover:underline">
        {more ? "Hide strengths & weaknesses ↑" : "Strengths & weaknesses →"}
      </button>
      {more && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12px]">
          <div><p className="font-semibold text-ink mb-1">Core strengths</p><TraitList items={bio.strengths} kind="strengths" /></div>
          <div><p className="font-semibold text-ink mb-1">Common weaknesses</p><TraitList items={bio.weaknesses} kind="weaknesses" /></div>
        </div>
      )}
    </div>
  );
};

const Famous = ({ sign, people }) => !people?.length ? null : (
  <div className="mt-4 pt-4 border-t border-[#e5e7eb]" data-testid={`famous-${sign}`}>
    <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink mb-2">{`Famous people born under ${sign}`}</p>
    {(
      <ul className="space-y-2">
        {people.map((e) => (
          <li key={e.id} className="flex items-center gap-3">
            <img src="/images/categories/birth.png" alt="" className="w-9 h-9 rounded-lg bg-paper shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-ink truncate">{personName(e.event_title)} <span className="font-normal text-ink-muted">- born {e.year}</span></p>
              <Link to={`/?date=${e.date_dd_mm}`} className="text-[12px] text-gold hover:underline">See what else happened on {prettyDate(e.date_dd_mm)} →</Link>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const SignCard = ({ sign, reading, traits, bio, famous, open, onToggle }) => (
  <article id={sign} data-testid={`horoscope-card-${sign}`} className="scroll-mt-24 bg-white border border-[#e5e7eb] rounded-2xl p-5 shadow-sm">
    <button type="button" onClick={onToggle} aria-expanded={open} data-testid={`horoscope-toggle-${sign}`} className="w-full flex items-center gap-4 text-left">
      <span className="w-14 h-14 shrink-0 rounded-xl bg-paper border border-[#e5e7eb] text-ink flex items-center justify-center text-[24px]">{ZODIAC_GLYPH[sign]}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-[20px] font-bold text-ink capitalize">{sign}</span>
        <span className="block text-[12px] text-ink-muted">{DATES[sign]}</span>
      </span>
      <ChevronDown className={`w-5 h-5 text-ink-muted transition-transform md:hidden ${open ? "rotate-180" : ""}`} />
    </button>
    <div className={`${open ? "block" : "hidden"} md:block mt-4`}>
      <p data-testid={`horoscope-reading-${sign}`} className="bg-paper border-l-4 border-gold rounded-r-xl px-4 py-3 text-[15px] leading-[1.7] text-[#333]">
        {reading || "Loading the cosmos..."}
      </p>
      <p className="mt-3 text-[13px] text-ink leading-relaxed">{traits || "Traits coming soon"}</p>
      <Bio sign={sign} bio={bio} />
      <Famous sign={sign} people={famous} />
    </div>
  </article>
);

export default function Horoscope() {
  const [data, setData] = useState(null);
  const [famous, setFamous] = useState({});
  const [open, setOpen] = useState({ aries: true });
  const now = new Date();

  useEffect(() => {
    setSeo({ title: `Today's Horoscope for all 12 star signs — ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} | OddlyTrue`, description: "Fresh daily horoscopes for Aries to Pisces, written each morning, plus each sign's traits, dates and the famous people born under it.", canonical: "https://oddlytrue.co/horoscope" });
  }, []);

  useEffect(() => {
    api.get("/horoscope/today").then((r) => setData(r.data));
    api.get("/horoscope/famous").then((r) => setFamous(r.data));
    const target = window.location.hash.replace("#", "");
    if (ZODIAC.includes(target)) {
      setOpen((o) => ({ ...o, [target]: true }));
      setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, []);

  return (
    <div className="min-h-screen bg-paper-light">
      <Header />
      <main data-testid="horoscope-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-muted mb-3">
          {now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-ink flex items-center gap-3">
          <Star className="w-7 h-7 text-gold" fill="#d4a017" /> Daily Horoscope
        </h1>
        <p className="mt-3 text-base text-ink-muted max-w-2xl">Personal guidance for your zodiac, not generic. Energy, love, career.</p>
        <AdSlot id="adsense-horoscope-top" slot="TOP" size="leaderboard" className="mt-6" />

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="horoscope-grid">
          {ZODIAC.map((s) => (
            <SignCard
              key={s}
              sign={s}
              reading={data?.signs?.[s]}
              traits={data?.traits?.[s]}
              bio={data?.bios?.[s]}
              famous={famous[s]}
              open={!!open[s]}
              onToggle={() => setOpen((o) => ({ ...o, [s]: !o[s] }))}
            />
          ))}
        </div>

        <div className="mt-10 text-center">
          <BirthdayLink testid="horoscope-email-cta" label="Get my birthday horoscope by email →" className="text-[15px]" source="horoscope_page" />
          <div className="mt-3"><BirthdayLink testid="birthday-link-horoscope-page" source="horoscope_page" /></div>
        </div>
      </main>
      <MobileBirthdayBar />
      <Footer />
    </div>
  );
}
